import TotalDispatcher from "./dispatcher";

//-----------------------------------------------
// Total CMS Generic Field
//-----------------------------------------------
export default class TotalField {

    constructor(container, options) {
		this.container = container;
		this.input     = this.container.querySelector("input,textarea,select");

		// Check if we're inside a deck-item
		this.deckItem = container.closest('.deck-item');
		this.isInDeck = !!this.deckItem;

		container.totalfield = this;

		this.type = container.dataset.type;
		this.property = this.input.name;

		// Extract label text from the label element
		const labelElement = this.container.querySelector('label');
		this.label = labelElement ? labelElement.textContent.trim() : this.property;

        // Define option defaults
        const defaults = {
			form        : null,
			sortOptions : false,
        };
        this.options = Object.assign({}, defaults, options);
        this.form = this.options.form;

		if (this.container.dataset.options) {
			this.options = Object.assign(this.options, JSON.parse(this.container.dataset.options));
		}

        // Delele the form from the options in case its used in JSON
        delete this.options.form;

        if (this.form) {
            this.api = this.form.api;
        }

		this.dispatcher = new TotalDispatcher(this.container);

		this.changeListener();
		if (this.options.sortOptions) {
			this.sortOptions();
		} else {
			this.cleanupDuplicateOptions();
		}
    }

	changeListener() {
		if (this.container.classList.contains("no-change-listener")) {
			// If the field has the no-change-listener class, don't add change listeners
			return;
		}

		// the change event happens more than once so the ID field can be updated for every change
		this.container.addEventListener("change", () => this.changed());
		// the input event happens once since the point is to mark the form as unsaved ASAP
		this.input.addEventListener("input", () => this.changed());
	}

	cleanupDuplicateOptions() {
		const container = this.container.querySelector("select,datalist");
		if (!container) return;
		const options = Array.from(container.querySelectorAll("option"));
		const values = [];
		options.forEach((option) => {
			if (values.includes(option.value)) {
				return option.remove();
			}
			values.push(option.value);
		});
	}

	sortOptions() {
		const container = this.container.querySelector("select,datalist");
		if (!container) return;
		if (container.querySelector("optgroup")) {
			const optgroups = Array.from(container.querySelectorAll("optgroup"));
			optgroups.forEach((optgroup) => {
				const options = Array.from(optgroup.querySelectorAll("option"));
				options.sort((a, b) => a.text.localeCompare(b.text));
				options.forEach((option) => optgroup.appendChild(option));
			});
			this.cleanupDuplicateOptions();
			return;
		}
		const options = Array.from(container.querySelectorAll("option"));
		options.sort((a, b) => a.text.localeCompare(b.text));
		options.forEach((option) => container.appendChild(option));

		this.cleanupDuplicateOptions();

		const placeholder = container.querySelector(".placeholder");
		if (placeholder) container.insertBefore(placeholder, container.firstChild);
	}

	isSubField() {
		// Filter for determining if a field is a subproperty of another field
		// Need to look at parentNode since closest also looks at self
		// Need to also look for cms-modal since droplets modify the DOM
		// and looking for .form-field is not enough
		return this.container.parentNode.closest(".form-field") ||
			this.container.parentNode.closest(".cms-modal") ? true : false;
	}

	isDroplet() {
		return (this.droplet && typeof this.droplet === "object");
	}

	isTiptap() {
		return (this.tiptap && typeof this.tiptap === "object");
	}

    getValue() {
        return this.input.value;
    }

    setValue(value) {
        this.input.value = value;
		this.changed();
    }

	clearValue() {
		this.setValue("");
	}

	isUnsaved() {
		return this.container.classList.contains("unsaved");
	}

    changed() {
		const hadError = this.container.classList.contains("error");
		this.input.setCustomValidity("");
		this.container.classList.remove("error");

		// Check if value actually changed first - if not, return early
		if (this.storedValue === this.getValue()) return;

		// Value changed - update stored value and dispatch event
		this.storedValue = this.getValue();
		this.container.classList.add("unsaved");

		// Always dispatch field-change when value changes
		// This ensures form state is updated (e.g., from "error" to "unsaved")
		if (this.isSubField()) {
			this.dispatcher.dispatchEvent("subfield-change", { field: this });
			return;
		}
		this.dispatcher.dispatchEvent("field-change", { field: this });
    }

	validate() {
		if (!this.isVisible()) return true;
		if (this.input.checkValidity()) return true;
		this.input.reportValidity();
		this.error(this.input.validationMessage);
		return false;
	}

	saved() {
		this.container.classList.remove("unsaved");
	}

	error(message) {
		this.container.classList.add("error");
		this.dispatcher.dispatchEvent("field-error", { field: this, message: message });
		console.warn(`Field Error: ${this.property} - ${message}`);
    }

	//-------------------------
	// Visibility Methods
	//-------------------------
	hide() {
		this.container.style.display = 'none';
		this.container.classList.remove('field-visible');
		this.container.classList.add('field-hidden');
		this.input.setCustomValidity("");
		this.container.classList.remove("error");
		this.disableValidation();
	}

	show() {
		this.container.style.display = '';
		this.container.classList.remove('field-hidden');
		this.container.classList.add('field-visible');
		this.enableValidation();
	}

	isVisible() {
		return !this.container.classList.contains('field-hidden');
	}

	isHidden() {
		return this.container.classList.contains('field-hidden');
	}

	enableValidation() {
		const inputs = this.container.querySelectorAll('input, select, textarea');
		inputs.forEach(input => {
			// Restore original required state
			if (input.dataset.originalRequired !== undefined) {
				if (input.dataset.originalRequired === 'true') {
					input.required = true;
				}
				delete input.dataset.originalRequired;
			}
		});
	}

	disableValidation() {
		const inputs = this.container.querySelectorAll('input, select, textarea');
		inputs.forEach(input => {
			// Save original required state
			if (input.required) {
				input.dataset.originalRequired = 'true';
				input.required = false;
			} else {
				input.dataset.originalRequired = 'false';
			}
		});
	}

    schema() {
        return {
            "type"  : this.type,
            "field" : "text"
        };
    }
}

// Radio Logic
// if (field.nodeName === "INPUT" && field.type === "radio" ) {
//     if (field.checked) {
//         return this.data[key] = field.value;
//     }
// }

// Checkboxes are a special case. We have to grab each checked values and put them into an array.
// else if (field.nodeName === "INPUT" && field.type === "checkbox") {
//     if (field.checked){
//         if (!this.data[key]){
//             this.data[key] = [];
//         }
//         return this.data[key].push(field.value);
//     }
// }
